import { Injectable, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { AirtableTs } from 'airtable-ts'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import * as dbTypes from './airtable.typings'

interface AirtableState {
  brands: dbTypes.Brand[]
  campaigns: dbTypes.Campaign[]
  campaign_templates: dbTypes.CampaignTemplate[]
  campaign_template_variables: dbTypes.CampaignTemplateVariable[]
  campaign_variables: dbTypes.CampaignVariable[]
  templates: dbTypes.Template[]
  components: dbTypes.Component[]
  component_attributes: dbTypes.ComponentAttribute[]
  attributes: dbTypes.Attribute[]
  pos: dbTypes.Po[]
}

export enum AirtableTables {
  BRAND = 'brands',
  CAMPAIGN = 'campaigns',
  CAMPAIGN_TEMPLATE = 'campaign_templates',
  CAMPAIGN_TEMPLATE_VARIABLE = 'campaign_template_variables',
  CAMPAIGN_VARIABLE = 'campaign_variables',
  TEMPLATE = 'templates',
  COMPONENT = 'components',
  COMPONENT_ATTRIBUTE = 'component_attributes',
  ATTRIBUTE = 'attributes',
  POS = 'pos',
}

// Map AirtableTables keys to corresponding AirtableState properties
type AirtableStateMap = {
  [K in AirtableTables]: AirtableState[K]
}

// Infer item type from AirtableStateMap
type AirtableItem<T extends AirtableTables> =
  AirtableStateMap[T] extends (infer U)[] ? U : never

@Injectable({
  providedIn: 'root',
})
export class AirtableService {
  static readonly API_KEY =
    'patbowdzq5HNBtxtp.50ea545d62825ffce215d9df0a15f20f1e9801f682d8a94eeabad4d6715ec580'

  private isInitialized = signal<boolean>(false)
  private isInitialized$

  private db = new AirtableTs({
    apiKey: AirtableService.API_KEY,
  })

  private state = signal<AirtableState>({
    [AirtableTables.BRAND]: [],
    [AirtableTables.CAMPAIGN]: [],
    [AirtableTables.CAMPAIGN_TEMPLATE]: [],
    [AirtableTables.CAMPAIGN_TEMPLATE_VARIABLE]: [],
    [AirtableTables.CAMPAIGN_VARIABLE]: [],
    [AirtableTables.TEMPLATE]: [],
    [AirtableTables.COMPONENT]: [],
    [AirtableTables.COMPONENT_ATTRIBUTE]: [],
    [AirtableTables.ATTRIBUTE]: [],
    [AirtableTables.POS]: [],
  })
  private state$

  private readonly _campaigns$ = new BehaviorSubject<any>(null)
  public readonly campaigns$ = this._campaigns$.asObservable()

  constructor() {
    this.state$ = toObservable(this.state)
    this.isInitialized$ = toObservable(this.isInitialized)

    this.initData().then(() => {
      console.log('Airtable data initialized.', this.state())
      this.isInitialized.set(true)
    })
  }

  async initData() {
    const brands = await this.db.scan(dbTypes.brandsTable)
    const campaigns = await this.db.scan(dbTypes.campaignsTable)
    const campaign_variables = await this.db.scan(
      dbTypes.campaignVariablesTable
    )
    const campaign_templates = await this.db.scan(
      dbTypes.campaignTemplatesTable
    )
    const campaign_template_variables = await this.db.scan(
      dbTypes.campaignTemplateVariablesTable
    )
    const templates = await this.db.scan(dbTypes.templatesTable)
    const components = await this.db.scan(dbTypes.componentsTable)
    const component_attributes = await this.db.scan(
      dbTypes.componentAttributesTable
    )
    const attributes = await this.db.scan(dbTypes.attributesTable)
    const pos = await this.db.scan(dbTypes.posTable)

    const state = {
      brands,
      campaigns,
      campaign_variables,
      campaign_templates,
      campaign_template_variables,
      templates,
      components,
      component_attributes,
      attributes,
      pos,
    }

    await this.state.set(state)
  }

  async refreshAfterCampaignCreation() {
    const campaigns = await this.db.scan(dbTypes.campaignsTable)
    const campaign_templates = await this.db.scan(
      dbTypes.campaignTemplatesTable
    )

    const currentState = this.state()

    const state = {
      ...currentState,
      campaigns,
      campaign_templates,
    }

    await this.state.set(state)
  }

  public async createCampaign(
    name: string,
    description: string,
    startDate: number,
    endDate: number,
    brand: string
  ) {
    return this.db.insert(dbTypes.campaignsTable, {
      name,
      description,
      startDate,
      endDate,
      status: 'in progress',
      brand: [brand],
    })
  }

  public async createCampaignTemplate(
    templateId: string,
    campaignId: string,
    name: string
  ) {
    return this.db.insert(dbTypes.campaignTemplatesTable, {
      templates: [templateId],
      campaign: [campaignId],
      name,
    })
  }

  public async createCampaignVariable(
    attributeId: string,
    campaignId: string,
    value: string
  ) {
    return this.db.insert(dbTypes.campaignVariablesTable, {
      attribute: [attributeId],
      campaign: [campaignId],
      value,
    })
  }

  public getState() {
    return this.isInitialized$.pipe(
      filter((isInitialized) => isInitialized),
      switchMap(() => this.state$)
    )
  }

  public getCampaigns(): Observable<any> {
    return this.getState().pipe(
      map((state) => {
        return state.campaigns.map((campaign) => {
          // Hydrate brand (single)
          const brand = state.brands.find(
            (brand) => brand.id === campaign.brand[0]
          )

          // Hydrate campaign variables (multiple)
          const campaignVariables = campaign.campaignVariables
            .map((variableId) =>
              state.campaign_variables.find(
                (variable) => variable.id === variableId
              )
            )
            .filter(Boolean)

          // Hydrate campaign templates (multiple)
          const campaignTemplates = campaign.campaignTemplates
            .map((templateId) => {
              const campaignTemplate = state.campaign_templates.find(
                (ct) => ct.id === templateId
              )
              if (!campaignTemplate) return null

              const templates = campaignTemplate.templates
                .map((templateId) => {
                  const template = state.templates.find(
                    (t) => t.id === templateId
                  )

                  const components = template?.components
                    .map((componentId) => {
                      const component = state.components.find(
                        (comp) => comp.id === componentId
                      )

                      // Hydrate attributes
                      const attributes = component?.componentAttributes
                        .map((componentAttributeId) => {
                          const componentAttribute =
                            state.component_attributes.find(
                              (attr) => attr.id === componentAttributeId
                            )

                          const attribute = state.attributes.find(
                            (attr) =>
                              attr.id === componentAttribute?.attribute[0]
                          )

                          // Link attribute relationships
                          const linkedCampaignTemplateVariables =
                            attribute?.campaignTemplateVariables
                              .map((variableId) =>
                                state.campaign_template_variables.find(
                                  (v) => v.id === variableId
                                )
                              )
                              .filter(Boolean)

                          const linkedCampaignVariables =
                            attribute?.campaignVariables
                              .map((variableId) =>
                                state.campaign_variables.find(
                                  (v) => v.id === variableId
                                )
                              )
                              .filter(Boolean)

                          const linkedComponents = (attribute?.components || [])
                            .map((compId) =>
                              state.components.find((c) => c.id === compId)
                            )
                            .filter(Boolean)

                          let options
                          try {
                            options = JSON.parse(
                              componentAttribute?.options || ''
                            )
                          } catch (error) {
                            console.error(
                              `Cannot parse options on attribute ${componentAttribute?.id}`
                            )
                          }

                          return {
                            options,
                            ...attribute,
                            componentAttribute,
                            campaignTemplateVariables:
                              linkedCampaignTemplateVariables,
                            campaignVariables: linkedCampaignVariables,
                            components: linkedComponents,
                          }
                        })
                        .filter(Boolean)

                      let options
                      try {
                        options = JSON.parse(component?.options || '')
                      } catch (error) {
                        console.error(
                          `Cannot parse options on attribute ${component?.id}`
                        )
                      }

                      return {
                        ...component,
                        options,
                        attributes,
                      }
                    })
                    .filter(Boolean)

                  return {
                    ...template,
                    components,
                  }
                })
                .filter(Boolean)

              const campaignTemplateVariables =
                campaignTemplate.campaignTemplateVariables
                  .map((variableId) =>
                    state.campaign_template_variables.find(
                      (variable) => variable.id === variableId
                    )
                  )
                  .filter(Boolean)

              return {
                ...campaignTemplate,
                templates,
                campaignTemplateVariables,
              }
            })
            .filter(Boolean)

          return {
            ...campaign,
            brand,
            campaignVariables,
            campaignTemplates,
          }
        })
      })
    )
  }

  public getCampaignById(id: string): Observable<any> {
    return this.getCampaigns().pipe(
      map((campaigns) => campaigns.find((campaign: any) => campaign.id === id))
    )
  }

  public async updateCampaignVariableById(
    variableId: string,
    newValue: any
  ): Promise<void> {
    const currentState = this.state()

    // Find and update the campaign variable
    const updatedCampaignVariables = currentState.campaign_variables.map(
      (variable) => {
        if (variable.id === variableId) {
          return { ...variable, value: newValue } // Update the value
        }
        return variable // Keep other variables unchanged
      }
    )

    // Update the state with the modified campaign variables
    this.state.set({
      ...currentState,
      campaign_variables: updatedCampaignVariables,
    })

    console.log(
      `Campaign variable with ID ${variableId} updated to:`,
      newValue,
      updatedCampaignVariables
    )

    // Save the update to Airtable
    try {
      const updatedRecord = await this.db.update(
        dbTypes.campaignVariablesTable,
        {
          id: variableId,
          value: newValue, // Assuming the Airtable field is named 'value'
        }
      )

      console.log(
        `Campaign variable with ID ${variableId} saved to Airtable:`,
        updatedRecord
      )
    } catch (error) {
      console.error(
        `Failed to save campaign variable with ID ${variableId} to Airtable:`,
        error
      )
    }
  }

  public getClients(): Observable<dbTypes.Brand[]> {
    return this.getState().pipe(map((state) => state.brands))
  }

  public getTemplates(): Observable<any[]> {
    return this.getState().pipe(
      map((state) => {
        return state.templates.map((template) => {
          // Hydrate POS (single)
          const pos = state.pos.find((po) => po.id === template.pos[0])

          const components = template?.components
            .map((componentId) => {
              const component = state.components
                .map((comp) => {
                  let options = {
                    top: 0,
                    left: 0,
                    height: 0,
                    width: 0,
                  }
                  try {
                    options = JSON.parse(comp.options)
                  } catch (error) {
                    console.error(
                      `Cannot parse options on template ${template.id}`
                    )
                  }

                  return {
                    ...comp,
                    options,
                  }
                })
                .find((comp) => comp.id === componentId)

              // Hydrate attributes
              const attributes = component?.componentAttributes
                .map((componentAttributeId) => {
                  const componentAttribute = state.component_attributes.find(
                    (attr) => attr.id === componentAttributeId
                  )

                  const attribute = state.attributes.find(
                    (attr) => attr.id === componentAttribute?.attribute[0]
                  )

                  // Link attribute relationships
                  const linkedCampaignTemplateVariables =
                    attribute?.campaignTemplateVariables
                      .map((variableId) =>
                        state.campaign_template_variables.find(
                          (v) => v.id === variableId
                        )
                      )
                      .filter(Boolean)

                  const linkedCampaignVariables = attribute?.campaignVariables
                    .map((variableId) =>
                      state.campaign_variables.find((v) => v.id === variableId)
                    )
                    .filter(Boolean)

                  const linkedComponents = (attribute?.components || [])
                    .map((compId) =>
                      state.components.find((c) => c.id === compId)
                    )
                    .filter(Boolean)

                  let options
                  try {
                    options = JSON.parse(componentAttribute?.options || '')
                  } catch (error) {
                    console.error(
                      `Cannot parse options on attribute ${componentAttribute?.id}`
                    )
                  }

                  return {
                    options,
                    ...attribute,
                    componentAttribute,
                    campaignTemplateVariables: linkedCampaignTemplateVariables,
                    campaignVariables: linkedCampaignVariables,
                    components: linkedComponents,
                  }
                })
                .filter(Boolean)

              return {
                ...component,
                attributes,
              }
            })
            .filter(Boolean)

          return {
            ...template,
            pos,
            components,
          }
        })
      })
    )
  }

  public getAttributes(): Observable<any[]> {
    return this.getState().pipe(
      map((state) => {
        return state.templates.map((template) => {
          // Hydrate POS (single)
          const pos = state.pos.find((po) => po.id === template.pos[0])

          const components = template?.components
            .map((componentId) => {
              const component = state.components.find(
                (comp) => comp.id === componentId
              )

              // Hydrate attributes
              const attributes = component?.componentAttributes
                .map((componentAttributeId) => {
                  const componentAttribute = state.component_attributes.find(
                    (attr) => attr.id === componentAttributeId
                  )

                  const attribute = state.attributes.find(
                    (attr) => attr.id === componentAttribute?.attribute[0]
                  )

                  // Link attribute relationships
                  const linkedCampaignTemplateVariables =
                    attribute?.campaignTemplateVariables
                      .map((variableId) =>
                        state.campaign_template_variables.find(
                          (v) => v.id === variableId
                        )
                      )
                      .filter(Boolean)

                  const linkedCampaignVariables = attribute?.campaignVariables
                    .map((variableId) =>
                      state.campaign_variables.find((v) => v.id === variableId)
                    )
                    .filter(Boolean)

                  const linkedComponents = (attribute?.components || [])
                    .map((compId) =>
                      state.components.find((c) => c.id === compId)
                    )
                    .filter(Boolean)

                  let options
                  try {
                    options = JSON.parse(componentAttribute?.options || '')
                  } catch (error) {
                    console.error(
                      `Cannot parse options on attribute ${componentAttribute?.id}`
                    )
                  }

                  return {
                    options,
                    ...attribute,
                    componentAttribute,
                    campaignTemplateVariables: linkedCampaignTemplateVariables,
                    campaignVariables: linkedCampaignVariables,
                    components: linkedComponents,
                  }
                })
                .filter(Boolean)

              return {
                ...component,
                attributes,
              }
            })
            .filter(Boolean)

          return {
            ...template,
            pos,
            components,
          }
        })
      })
    )
  }
}
